import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { API } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Download, FileSpreadsheet, AlertTriangle, ArrowLeft, ImageOff, Globe, FileText } from "lucide-react";

// Iter63A-2 — Restaurant Menu Discovery Engine UI.
// Backend pipeline (Playwright Cloudflare bypass + Claude Sonnet 4.5
// vision OCR + multi-source merge) runs as an ASYNC job because it
// routinely exceeds the gateway's 60s edge timeout. This component:
//   1. POST /api/retail/menu-discovery/run         → { job_id }
//   2. polls /api/retail/menu-discovery/status/{id} every 2s
//   3. on completion, renders an Extraction Summary card (per-source
//      counts + merged total) and an Operator Review grid with
//      thumbnails, name, category, price, source provenance, and
//      confidence. Operator can deselect, edit, then:
//      - Download CSV  (GET /export/{job_id}.csv)
//      - Download XLSX (GET /export/{job_id}.xlsx)
//      - Commit To Catalog (POST /retail/catalogs/{cid}/extract/commit)
// Source: noonmirch.com is the acceptance benchmark.

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 180;  // 6 minutes max

const SOURCE_BADGES = {
  pdf:        { bg: "bg-rose-50",    fg: "text-rose-800",    bd: "border-rose-200",    label: "PDF" },
  toasttab:   { bg: "bg-orange-50",  fg: "text-orange-800",  bd: "border-orange-200",  label: "ToastTab" },
  popmenu:    { bg: "bg-purple-50",  fg: "text-purple-800",  bd: "border-purple-200",  label: "PopMenu" },
  chownow:    { bg: "bg-cyan-50",    fg: "text-cyan-800",    bd: "border-cyan-200",    label: "ChowNow" },
  square:     { bg: "bg-slate-50",   fg: "text-slate-800",   bd: "border-slate-200",   label: "Square" },
  clover:     { bg: "bg-emerald-50", fg: "text-emerald-800", bd: "border-emerald-200", label: "Clover" },
  ubereats:   { bg: "bg-lime-50",    fg: "text-lime-800",    bd: "border-lime-200",    label: "Uber Eats" },
  doordash:   { bg: "bg-red-50",     fg: "text-red-800",     bd: "border-red-200",     label: "DoorDash" },
  grubhub:    { bg: "bg-amber-50",   fg: "text-amber-800",   bd: "border-amber-200",   label: "Grubhub" },
  html:       { bg: "bg-blue-50",    fg: "text-blue-800",    bd: "border-blue-200",    label: "Website" },
};

function sourceBadge(type) {
  return SOURCE_BADGES[type] || { bg: "bg-slate-50", fg: "text-slate-700", bd: "border-slate-200", label: type || "source" };
}

function safeHostname(url) {
  try { return new URL(url).hostname; } catch { return url; }
}

export default function MenuDiscoveryPanel({ catalog, isDraft, onCommitted }) {
  const [step, setStep] = useState("input");  // input | running | review
  const [rootUrl, setRootUrl] = useState("");
  const [maxSources, setMaxSources] = useState(8);
  const [error, setError] = useState("");

  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);  // full status payload
  const [polling, setPolling] = useState(false);
  const pollCount = useRef(0);
  const pollTimer = useRef(null);

  // Review-grid state
  const [rows, setRows] = useState([]);  // [{...row, _selected}]
  const [committing, setCommitting] = useState(false);

  const isValidUrl = /^https?:\/\//i.test(rootUrl.trim());

  // --- cleanup polling on unmount ----
  useEffect(() => () => { if (pollTimer.current) clearTimeout(pollTimer.current); }, []);

  // --- Step 1: kick off discovery job ---
  const runDiscovery = async () => {
    if (!isValidUrl) {
      setError("Please enter a valid http(s) URL.");
      return;
    }
    setError("");
    setStep("running");
    setJobStatus(null);
    setRows([]);
    pollCount.current = 0;
    try {
      const r = await api.post("/retail/menu-discovery/run", {
        url: rootUrl.trim(),
        max_sources: maxSources,
      });
      setJobId(r.data.job_id);
      setJobStatus({ status: "processing", started_at: r.data.started_at });
      setPolling(true);
      schedulePoll(r.data.job_id);
    } catch (e) {
      setStep("input");
      const msg = e.response?.data?.detail || e.message || "Failed to start discovery.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Discovery start failed.");
    }
  };

  const schedulePoll = (id) => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = setTimeout(() => pollOnce(id), POLL_INTERVAL_MS);
  };

  const pollOnce = async (id) => {
    pollCount.current += 1;
    try {
      const r = await api.get(`/retail/menu-discovery/status/${id}`);
      setJobStatus(r.data);
      if (r.data.is_terminal) {
        setPolling(false);
        if (r.data.status === "completed") {
          const merged = Array.isArray(r.data.merged_rows) ? r.data.merged_rows : [];
          setRows(merged.map((m) => ({ ...m, _selected: true })));
          setStep("review");
          toast.success(`Discovery complete — ${merged.length} merged items.`);
        } else {
          toast.error(`Discovery failed: ${r.data.error || "unknown error"}`);
          setStep("input");
        }
        return;
      }
      if (pollCount.current >= MAX_POLLS) {
        setPolling(false);
        toast.error("Discovery timed out. Please try again or reduce the URL depth.");
        setStep("input");
        return;
      }
      schedulePoll(id);
    } catch (e) {
      setPolling(false);
      const msg = e.response?.data?.detail || e.message || "Polling failed.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Polling failed.");
      setStep("input");
    }
  };

  // --- Review-grid handlers ---
  const toggleRow = (i) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, _selected: !row._selected } : row));
  const editField = (i, k, v) => setRows((r) => r.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const selectedRows = rows.filter((r) => r._selected);

  // --- Downloads (use job-cached results, never re-run the pipeline) ---
  const downloadBlob = async (kind) => {
    if (!jobId) return;
    try {
      const url = `${API}/retail/menu-discovery/export/${jobId}.${kind}`;
      const token = localStorage.getItem("ren_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `menu_discovery_${jobId}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success(`${kind.toUpperCase()} downloaded.`);
    } catch (e) {
      toast.error(`${kind.toUpperCase()} download failed.`);
    }
  };

  // --- Commit to Catalog (reuses existing /extract/commit endpoint) ---
  const commitToCatalog = async () => {
    if (!isDraft) {
      toast.error("Catalog must be in Draft status to commit.");
      return;
    }
    const toCommit = selectedRows.map(({ _selected, ...rest }) => ({
      name: rest.name || "",
      price: rest.price ?? null,
      currency: rest.currency || "USD",
      category: rest.category || "",
      description: rest.description || "",
      image_url: rest.image_url || "",
      available: rest.available !== false,
    }));
    if (toCommit.length === 0) {
      toast.error("Nothing selected to commit.");
      return;
    }
    setCommitting(true);
    try {
      const r = await api.post(`/retail/catalogs/${catalog.id}/extract/commit`, {
        products: toCommit,
      });
      toast.success(`Committed: ${r.data.inserted} items (rejected: ${r.data.rejected_count}).`);
      if (onCommitted) await onCommitted();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.error || e.message);
      toast.error(`Commit failed: ${msg}`);
    } finally {
      setCommitting(false);
    }
  };

  const reset = () => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    setStep("input");
    setRows([]);
    setJobStatus(null);
    setJobId(null);
    setRootUrl("");
    setError("");
    pollCount.current = 0;
  };

  // ============== render ==============
  if (step === "input") {
    return (
      <div className="space-y-4" data-testid="retail-menu-discovery-panel">
        <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-violet-600 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-violet-900">Menu Discovery Engine</h4>
              <p className="text-xs text-violet-800/80 mt-1 max-w-2xl">
                Paste a restaurant homepage. The engine finds every menu source
                (PDF, ToastTab, PopMenu, ChowNow, Square, Clover, website HTML),
                bypasses Cloudflare with Playwright, and uses Claude Sonnet 4.5
                vision-OCR on image-PDFs. Items are merged, deduped, and ready
                for operator review.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-700">Restaurant website URL</label>
          <input
            type="url"
            placeholder="https://www.noonmirch.com"
            value={rootUrl}
            onChange={(e) => setRootUrl(e.target.value)}
            className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
            data-testid="retail-menu-discovery-url-input"
          />
          <div className="flex items-center gap-3 pt-2">
            <label className="text-xs text-slate-600">Max sources:</label>
            <input
              type="number"
              min="1"
              max="12"
              value={maxSources}
              onChange={(e) => setMaxSources(Number(e.target.value || 8))}
              className="w-20 text-sm border border-slate-300 rounded-md px-2 py-1"
              data-testid="retail-menu-discovery-max-sources"
            />
            <span className="text-[11px] text-slate-500">PDFs + platform pages + HTML root.</span>
          </div>
          {error && (
            <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1 flex items-center gap-1" data-testid="retail-menu-discovery-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={runDiscovery}
          disabled={!isValidUrl}
          className="bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="retail-menu-discovery-run-btn"
        >
          <Sparkles className="w-4 h-4" /> Run Discovery
        </button>
      </div>
    );
  }

  if (step === "running") {
    return <RunningCard jobStatus={jobStatus} jobId={jobId} onCancel={reset} pollCountRef={pollCount} />;
  }

  // --- Review step ---
  const perSource = jobStatus?.per_source_rows || [];
  const stats = jobStatus?.stats || {};
  const sourcesDetected = jobStatus?.sources_detected || [];
  const mergedCount = rows.length;
  const categoriesSet = new Set(rows.map((r) => (r.category || "").trim()).filter(Boolean));
  const imagesCount = rows.filter((r) => r.image_url).length;
  const totalRaw = (stats.n_per_source || []).reduce((a, b) => a + b, 0);
  const duplicatesRemoved = Math.max(0, totalRaw - mergedCount);

  return (
    <div className="space-y-4" data-testid="retail-menu-discovery-review">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={reset}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="retail-menu-discovery-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> New discovery
        </button>
        <p className="text-[11px] text-slate-500" data-testid="retail-menu-discovery-source-url">
          Source: <span className="font-mono">{jobStatus?.url}</span>
        </p>
      </div>

      {/* ---- Extraction Summary card ---- */}
      <div
        className="bg-slate-50 border border-slate-200 rounded-xl p-4"
        data-testid="retail-menu-discovery-summary"
      >
        <h4 className="text-sm font-semibold text-slate-900 mb-3">Extraction Summary</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2 mb-3">
          <SummaryStat label="Sources" value={sourcesDetected.length} testId="retail-menu-discovery-stat-sources" />
          <SummaryStat label="Raw items" value={totalRaw} testId="retail-menu-discovery-stat-raw" />
          <SummaryStat label="Merged total" value={mergedCount} testId="retail-menu-discovery-stat-merged" tint="violet" />
          <SummaryStat label="Categories" value={categoriesSet.size} testId="retail-menu-discovery-stat-categories" />
          <SummaryStat label="Images" value={imagesCount} testId="retail-menu-discovery-stat-images" />
          <SummaryStat label="Duplicates removed" value={duplicatesRemoved} testId="retail-menu-discovery-stat-duplicates" />
        </div>
        {perSource.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Per-source breakdown</p>
            <ul className="text-xs text-slate-700 space-y-1">
              {perSource.map((ps, i) => {
                const badge = sourceBadge(ps.source);
                return (
                  <li key={i} className="flex items-center gap-2" data-testid={`retail-menu-discovery-source-${i}`}>
                    <span className="text-slate-400 w-12">Source {i + 1}:</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md border ${badge.bg} ${badge.fg} ${badge.bd}`}>
                      {ps.source === "pdf" ? <FileText className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                      {badge.label}
                    </span>
                    <span className="font-mono text-[11px] text-slate-500 truncate max-w-xs">{safeHostname(ps.url)}</span>
                    <span className="ml-auto font-semibold tabular-nums" data-testid={`retail-menu-discovery-source-count-${i}`}>
                      {(ps.rows || []).length} items
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* ---- Action bar ---- */}
      <div className="flex flex-wrap items-center gap-2 justify-between bg-white border border-slate-200 rounded-xl p-3">
        <p className="text-xs text-slate-500">
          <span className="font-semibold text-slate-900">{selectedRows.length}</span> of {mergedCount} selected for export / commit.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => downloadBlob("csv")}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="retail-menu-discovery-download-csv-btn"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
          <button
            type="button"
            onClick={() => downloadBlob("xlsx")}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="retail-menu-discovery-download-xlsx-btn"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> XLSX
          </button>
          <button
            type="button"
            onClick={commitToCatalog}
            disabled={committing || selectedRows.length === 0 || !isDraft}
            className="bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium px-3 py-1.5 rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="retail-menu-discovery-commit-btn"
            title={!isDraft ? "Catalog must be in Draft status to commit." : ""}
          >
            {committing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Commit {selectedRows.length} to catalog
          </button>
        </div>
      </div>
      {!isDraft && (
        <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
          Catalog is not in Draft status — CSV/XLSX export still works, but
          “Commit to Catalog” is disabled.
        </p>
      )}

      {/* ---- Review grid ---- */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[28px_56px_2fr_120px_90px_110px_60px] gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">
          <div></div>
          <div>Image</div>
          <div>Name & description</div>
          <div>Category</div>
          <div>Price</div>
          <div>Source</div>
          <div className="text-right">Conf.</div>
        </div>
        <div className="max-h-[480px] overflow-y-auto divide-y divide-slate-100">
          {rows.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="retail-menu-discovery-empty">
              No items extracted. Try a different URL or increase max sources.
            </div>
          )}
          {rows.map((row, idx) => {
            const badge = sourceBadge(perSource.find((p) => p.url === row.source_url)?.source || "html");
            return (
              <div
                key={idx}
                className={"grid grid-cols-[28px_56px_2fr_120px_90px_110px_60px] gap-2 px-3 py-2 items-center " + (row._selected ? "" : "opacity-50")}
                data-testid={`retail-menu-discovery-row-${idx}`}
              >
                <input
                  type="checkbox"
                  checked={row._selected}
                  onChange={() => toggleRow(idx)}
                  className="w-4 h-4 accent-violet-600"
                  data-testid={`retail-menu-discovery-row-select-${idx}`}
                />
                <Thumb url={row.image_url} idx={idx} />
                <div className="min-w-0">
                  <input
                    className="w-full text-sm border border-slate-200 rounded-md px-2 py-1 mb-1 focus:outline-none focus:ring-1 focus:ring-violet-400"
                    value={row.name || ""}
                    onChange={(e) => editField(idx, "name", e.target.value)}
                    data-testid={`retail-menu-discovery-name-${idx}`}
                  />
                  <input
                    className="w-full text-[11px] text-slate-500 border border-slate-100 rounded-md px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-violet-300"
                    value={row.description || ""}
                    onChange={(e) => editField(idx, "description", e.target.value)}
                    placeholder="(description)"
                  />
                </div>
                <input
                  className="text-xs border border-slate-200 rounded-md px-2 py-1"
                  value={row.category || ""}
                  onChange={(e) => editField(idx, "category", e.target.value)}
                  placeholder="(none)"
                  data-testid={`retail-menu-discovery-category-${idx}`}
                />
                <input
                  type="number"
                  step="0.01"
                  className="text-xs border border-slate-200 rounded-md px-2 py-1 tabular-nums"
                  value={row.price ?? ""}
                  onChange={(e) => editField(idx, "price", e.target.value === "" ? null : Number(e.target.value))}
                  data-testid={`retail-menu-discovery-price-${idx}`}
                />
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-md border inline-flex items-center justify-center ${badge.bg} ${badge.fg} ${badge.bd}`}
                  title={row.source_url || ""}
                  data-testid={`retail-menu-discovery-source-${idx}`}
                >
                  {badge.label}
                </span>
                <span className="text-[10px] text-slate-500 text-right tabular-nums" data-testid={`retail-menu-discovery-conf-${idx}`}>
                  {row.confidence ? Math.round(row.confidence * 100) + "%" : "—"}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ label, value, tint, testId }) {
  const colors = {
    violet: "bg-violet-50 text-violet-900 border-violet-200",
    amber:  "bg-amber-50 text-amber-900 border-amber-200",
  }[tint] || "bg-white text-slate-900 border-slate-200";
  return (
    <div className={`border rounded-lg px-2 py-1.5 ${colors}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-base font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function RunningCard({ jobStatus, jobId, onCancel }) {
  const elapsedRef = useRef(null);
  const startedAt = jobStatus?.started_at;
  useEffect(() => {
    const startMs = startedAt ? new Date(startedAt).getTime() : Date.now();
    const tick = () => {
      if (elapsedRef.current) {
        const s = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
        elapsedRef.current.textContent = `Elapsed: ${s}s${jobId ? ` · job ${jobId.slice(0, 12)}…` : ""}`;
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [startedAt, jobId]);
  return (
    <div className="space-y-4" data-testid="retail-menu-discovery-running">
      <div className="bg-white border border-violet-200 rounded-xl p-6 flex items-center gap-4">
        <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-slate-900">Discovering menu sources…</p>
          <p className="text-xs text-slate-600 mt-1">
            Bypassing Cloudflare, parsing PDFs, calling Claude Sonnet 4.5 vision.
            This can take 1–4 minutes for complex restaurant sites.
          </p>
          <p className="text-[11px] text-slate-400 mt-1" ref={elapsedRef} data-testid="retail-menu-discovery-elapsed">
            Elapsed: 0s {jobId && `· job ${jobId.slice(0, 12)}…`}
          </p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-slate-500 hover:text-slate-900 underline"
          data-testid="retail-menu-discovery-cancel-btn"
        >Cancel</button>
      </div>
    </div>
  );
}

function Thumb({ url, idx }) {
  const [broken, setBroken] = useState(false);
  if (!url || broken) {
    return (
      <div
        className="w-12 h-12 rounded-md border border-dashed border-slate-200 grid place-items-center text-[9px] text-slate-400"
        data-testid={`retail-menu-discovery-thumb-empty-${idx}`}
      >
        <ImageOff className="w-3.5 h-3.5" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      className="w-12 h-12 object-cover rounded-md border border-slate-200"
      onError={() => setBroken(true)}
      data-testid={`retail-menu-discovery-thumb-${idx}`}
    />
  );
}
